/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { css, cx } from "@panda/css";

import { CosmosText, hasContent, CosmosTextVariantEnum, CosmosRichText } from "@components/elements";
import type { CosmosVariableRichText } from "@components/elements";
import type { CSSProperties } from "react";

export interface CosmosTitleProps {
	title?: CosmosVariableRichText | null;
	className?: string;
	style?: CSSProperties;
}

const cosmosTitleStyles = css({
	"&& p": {
		marginBlock: "0",
	},
	// [MW-7845] remove wrapping for single paragraph
	"&& > p:first-child:last-child": {
		display: "contents",
	},
	"&& em": {
		fontStyle: "italic",
	},
});

export const CosmosTitle = ({ title, className, style }: CosmosTitleProps): React.JSX.Element | null => {
	if (!title || (!title.text && !title.html)) {
		return null;
	}

	// Check if title has actual content (not just empty HTML tags or whitespace)
	if (!hasContent(title)) {
		return null;
	}

	if (title?.html && title?.html.includes("</")) {
		// Only render HTML if it has content (already checked by hasContent above)
		//remove any leading line breaks
		const cleanHtml = title.html.replace(/^<br \/>/, "").replace(/^<br\/>/, "");
		return (
			<CosmosRichText
				{...title}
				variant={title?.variant || CosmosTextVariantEnum.heading2XL}
				color={title?.color || "eds.text.primary"}
				html={cleanHtml}
				className={cx("cosmos-title", className, cosmosTitleStyles)}
				style={style}
			/>
		);
	}

	// fallback to html if it doesn't have any tags and text field is empty
	const titleContent = title?.text?.trim() || title?.html?.trim();
	if (typeof titleContent === "string" && titleContent.length > 0) {
		return (
			<CosmosText
				variant={title?.variant || CosmosTextVariantEnum.heading2XL}
				color={title?.color || "eds.text.primary"}
				as={title?.as || undefined}
				className={cx("cosmos-title", className)}
				style={style}
			>
				{titleContent}
			</CosmosText>
		);
	}

	return null;
};
